/**
 * ProfileBio Component
 * 
 * Bio/About section for contractor profile
 */

'use client';

import { useTranslations, useLocale } from 'next-intl';
import { Typography } from '@/src/shared/ui/typography/Typography';
import { getLocalizedName, type Locale } from '@hummii/shared-types';

export interface ProfileBioProps {
  bio: string | null;
  experience: number | null;
  categories: Array<{
    id: string;
    nameEn: string;
    nameFr: string;
    slug: string;
  }>;
}

export function ProfileBio({ bio, experience, categories }: ProfileBioProps) {
  const t = useTranslations('profile');
  const locale = useLocale() as Locale;

  return (
    <div className="bg-background-card border border-border-primary rounded-card-lg p-6">
      <Typography variant="h3" weight="semibold" className="mb-4">
        {t('about')}
      </Typography>

      {bio ? (
        <Typography variant="body" color="secondary" className="mb-6 whitespace-pre-line">
          {bio}
        </Typography>
      ) : (
        <Typography variant="body" color="tertiary" className="mb-6 italic">
          {t('noBio')}
        </Typography>
      )}

      {/* Experience */}
      {experience !== null && (
        <div className="mb-4">
          <Typography variant="bodySm" weight="semibold" className="mb-1">
            {t('experience')}
          </Typography>
          <Typography variant="body" color="secondary">
            {t('yearsExperience', { years: experience })}
          </Typography>
        </div>
      )}

      {/* Categories */}
      {categories.length > 0 && (
        <div>
          <Typography variant="bodySm" weight="semibold" className="mb-2">
            {t('services')}
          </Typography>
          <div className="flex flex-wrap gap-2">
            {categories.map((category) => (
              <span
                key={category.id}
                className="px-3 py-1 bg-accent-primary/10 text-accent-primary rounded-full text-sm font-medium"
              >
                {getLocalizedName(category, locale)}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
